import React, {useEffect, useState} from 'react';
import {Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {useTranslation} from 'react-i18next';
import apiService from '@/service/api-service';
import {formatCurrency, formatDate, formatDateTime, formatDistance, formatDuration} from '@/utils/formatters';
import {ShiftData, TripData} from '@/components/shifts';
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from '@/components/ui/accordion';
import {useIsMobile} from "@/hooks/use-mobile.tsx";

interface AssignmentData {
  id: string;
  uuid: string;
  carVin: string;
  deviceManufacturerNumber: string;
  installedAt: any;
  removedAt: any;
  customerNumber?: string;
  [key: string]: any;
}

interface AssignmentWithoutLicensePlateDetailDialogProps {
  assignment: AssignmentData | null;
  open: boolean;
  onClose: () => void;
}

interface DateObject {
  date: string;
  timezone_type: number;
  timezone: string;
}

interface RawShiftData {
  id: string | null;
  vendorShiftId: number;
  deviceId: number;
  startAt: DateObject;
  endAt: DateObject;
  createdAt: DateObject;
  driverNumber: number;
  licenseNumber: string;
  sequenceNumber: number;
  mileageStart: number;
  mileageEnd: number;
  forHireDistance: number;
  hiredDistance: number;
  totalDistance: number;
  totalAmount: number;
  extrasAmount: number;
  totalAmountStart: number;
  totalAmountEnd: number;
  vendor: string;
  deviceType: number;
  tripsCount: number;
  gpsStamps: any[];
}

interface RawTripData {
  id: string | null;
  vendorTripId: number;
  deviceId: number;
  sequenceNumber: number;
  shiftSequenceNumber: number;
  startAt: DateObject;
  endAt: DateObject;
  createdAt: DateObject;
  totalAmount: number;
  extrasAmount: number;
  waitingTime: number;
  totalDistance: number;
  hiredDistance: number;
  onCallDistance: number;
  licenseNumber: string;
  driverNumber: number;
  isFlatRate: boolean;
  vendor: string;
  deviceType: number;
  tripType: number;
  gpsStamps: any[];
}

interface ApiResponse {
  shifts: {
    count: number;
    data: RawShiftData[];
  };
  trips: {
    count: number;
    data: RawTripData[];
  };
}

// Custom ShiftItem component that shows "Seriennummer" instead of "Kennzeichen"
const ShiftItemWithSerialNumber: React.FC<{ shift: ShiftData }> = ({ shift }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <Box sx={{ p: 1 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.driver')}
          </Typography>
          <Typography variant="body2">
            {shift.driver?.name || t('assignments.unknownDriver')}
            {shift.driver?.isSystemDriver && (
              <Chip
                label={t('assignments.systemDriver')}
                color="info"
                size="small"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Typography>
          {shift.driver?.gender && (
            <Typography variant="caption" color="text.secondary">
              {t(`assignments.gender.${shift.driver.gender}`)}
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.driverNumber')}
          </Typography>
          <Typography variant="body2">
            {shift.driver?.driverNumber || '-'}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assistant.deviceStep.serialNumber')}
          </Typography>
          <Typography variant="body2">
            {shift.licenseNumber}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.duration')}
          </Typography>
          <Typography variant="body2">
            {formatDuration(shift.duration)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.totalDistance')}
          </Typography>
          <Typography variant="body2">
            {formatDistance(shift.totalDistance)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.mileageStart')}
          </Typography>
          <Typography variant="body2">
            {formatDistance(shift.mileageStart)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.mileageEnd')}
          </Typography>
          <Typography variant="body2">
            {formatDistance(shift.mileageEnd)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.hiredDistance')}
          </Typography>
          <Typography variant="body2">
            {formatDistance(shift.hiredDistance)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('assignments.forHireDistance')}
          </Typography>
          <Typography variant="body2">
            {formatDistance(shift.forHireDistance)}
          </Typography>
        </Grid>
      </Grid>

      {shift.trips && shift.trips.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('assignments.trips')}
          </Typography>
          <Accordion type="single" collapsible className="w-full">
            {shift.trips.map((trip) => (
              <AccordionItem key={trip.uuid} value={trip.uuid}>
                <AccordionTrigger className="hover:no-underline">
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle2">
                        {t('assignments.trip')} #{trip.sequenceNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(trip.startAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {trip.isCancelled && (
                        <Chip
                          label={t('assignments.cancelled')}
                          color="error"
                          size="small"
                        />
                      )}
                      <Chip
                        label={formatCurrency(trip.finalTotalAmount)}
                        color="primary"
                        size="small"
                      />
                      <Chip
                        label={formatDistance(trip.totalDistance)}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>
                </AccordionTrigger>
                <AccordionContent>
                  <Box sx={{ p: 1 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('assignments.paymentMethod')}
                        </Typography>
                        <Typography variant="body2">
                          {t(`assignments.paymentMethods.${trip.paymentMethod}`)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

// Custom ShiftsList component that shows open shift indicator
const ShiftsListWithOpenIndicator: React.FC<{ shifts: ShiftData[], loading: boolean, error: string | null }> = ({ shifts, loading, error }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // Helper to check if shift is open based on endAt date
  const isShiftOpen = (endAt: string): boolean => {
    return endAt.startsWith('1900-01-01');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" my={2}>
        {error}
      </Typography>
    );
  }

  if (shifts.length === 0) {
    return (
      <Typography align="center" color="text.secondary" my={2}>
        {t('assignments.noShifts')}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Accordion type="single" collapsible className="w-full">
        {shifts.map((shift) => {
          const isOpen = isShiftOpen(shift.endAt);
          return (
            <AccordionItem key={shift.uuid} value={shift.uuid}>
              <AccordionTrigger className="hover:no-underline">
                <Box sx={{ width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="subtitle1">
                      {t('assignments.shift')} #{shift.sequenceNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(shift.startAt)}
                      {!isOpen && (
                        <> - {formatDateTime(shift.endAt)}</>
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {isOpen ? (
                      <Chip
                        label={t('assignments.openShift')}
                        color="warning"
                        size="small"
                      />
                    ) : (
                      <>
                        <Chip
                          label={formatCurrency(shift.finalTotalAmount)}
                          color="primary"
                          size="small"
                        />
                        <Chip
                          label={`${shift.tripsCount} ${t('assignments.trips')}`}
                          variant="outlined"
                          size="small"
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </AccordionTrigger>
              <AccordionContent>
                <ShiftItemWithSerialNumber key={shift.uuid} shift={shift} />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </Box>
  );
};

const AssignmentWithoutLicensePlateDetailDialog = ({ assignment, open, onClose }: AssignmentWithoutLicensePlateDetailDialogProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [shifts, setShifts] = useState<ShiftData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!assignment) return null;

  // Helper function to convert DateObject to ISO string
  const dateObjectToISO = (dateObj: DateObject): string => {
    // Parse the date string and timezone
    const dateStr = dateObj.date.replace(' ', 'T');
    const timezone = dateObj.timezone;
    return `${dateStr}${timezone}`;
  };

  // Helper function to check if a shift is open (not closed yet)
  const isOpenShift = (endAt: DateObject): boolean => {
    // Check if the date is 1900-01-01 in UTC
    const dateStr = endAt.date;
    return dateStr.startsWith('1900-01-01');
  };

  // Helper function to calculate duration in seconds
  const calculateDuration = (startAt: DateObject, endAt: DateObject): number => {
    const start = new Date(dateObjectToISO(startAt)).getTime();
    const end = new Date(dateObjectToISO(endAt)).getTime();
    const durationSeconds = Math.floor((end - start) / 1000);
    // Don't allow negative durations for open shifts
    return Math.max(0, durationSeconds);
  };

  // Helper function to match trips to shifts
  const matchTripsToShifts = (rawShifts: RawShiftData[], rawTrips: RawTripData[]): ShiftData[] => {
    // Create a map of trips by shift sequence number
    const tripsByShiftSequence = new Map<number, RawTripData[]>();
    rawTrips.forEach(trip => {
      const trips = tripsByShiftSequence.get(trip.shiftSequenceNumber) || [];
      trips.push(trip);
      tripsByShiftSequence.set(trip.shiftSequenceNumber, trips);
    });

    // Transform shifts and attach matching trips
    return rawShifts.map(rawShift => {
      const shiftTrips = tripsByShiftSequence.get(rawShift.sequenceNumber) || [];

      // Transform trips to match the expected format
      const transformedTrips: TripData[] = shiftTrips.map(trip => ({
        uuid: `trip-${trip.vendorTripId}`,
        sequenceNumber: trip.sequenceNumber,
        shiftSequenceNumber: trip.shiftSequenceNumber,
        startAt: dateObjectToISO(trip.startAt),
        endAt: dateObjectToISO(trip.endAt),
        duration: calculateDuration(trip.startAt, trip.endAt),
        totalAmount: trip.totalAmount,
        subtractedTotalAmount: 0, // Not provided in the API response
        finalTotalAmount: trip.totalAmount,
        extrasAmount: trip.extrasAmount,
        subtractedExtrasAmount: 0, // Not provided in the API response
        finalExtrasAmount: trip.extrasAmount,
        waitingTime: trip.waitingTime,
        totalDistance: trip.totalDistance,
        hiredDistance: trip.hiredDistance,
        onCallDistance: trip.onCallDistance,
        isCancelled: false, // Not provided in the API response
        licenseNumber: trip.licenseNumber,
        driverNumber: trip.driverNumber,
        paymentMethod: 'cash', // Default to cash if not provided
        isFlatRate: trip.isFlatRate,
      }));

      // Transform shift to match the expected format
      const shift: ShiftData = {
        uuid: `shift-${rawShift.vendorShiftId}`,
        sequenceNumber: rawShift.sequenceNumber,
        startAt: dateObjectToISO(rawShift.startAt),
        endAt: dateObjectToISO(rawShift.endAt),
        duration: calculateDuration(rawShift.startAt, rawShift.endAt),
        tripsCount: rawShift.tripsCount,
        mileageStart: rawShift.mileageStart,
        mileageEnd: rawShift.mileageEnd,
        forHireDistance: rawShift.forHireDistance,
        hiredDistance: rawShift.hiredDistance,
        totalDistance: rawShift.totalDistance,
        totalAmount: rawShift.totalAmount,
        subtractedTotalAmount: 0, // Not provided in the API response
        finalTotalAmount: rawShift.totalAmount,
        extrasAmount: rawShift.extrasAmount,
        subtractedExtrasAmount: 0, // Not provided in the API response
        finalExtrasAmount: rawShift.extrasAmount,
        licenseNumber: rawShift.licenseNumber,
        driverNumber: rawShift.driverNumber,
        driver: {
          driverNumber: rawShift.driverNumber,
          name: t('assignments.unknownDriver'), // Not provided in the API response
          gender: null,
          isSystemDriver: false,
        },
        trips: transformedTrips,
      };

      return shift;
    });
  };

  // Define fetchShifts outside of useEffect to avoid dependency issues
  const fetchShifts = async () => {
    if (!assignment.customerNumber || !assignment.uuid) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get(
        `/customers/${assignment.customerNumber}/installations/${assignment.uuid}/pull`
      );

      const data: ApiResponse = response.data;
      const transformedShifts = matchTripsToShifts(data.shifts.data, data.trips.data);
      setShifts(transformedShifts);
    } catch (err) {
      console.error('Failed to fetch shifts:', err);
      setError(t('errors.failedToLoadShifts'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && assignment && assignment.uuid) {
      fetchShifts();
    }
  }, [open, assignment?.uuid, assignment?.customerNumber]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ bgcolor: 'background.paper' }}>
        {t('assignments.details')}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'background.paper', pt: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('assistant.vehicleIdentificationStep.vin')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {assignment.carVin}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('devices.table.deviceSerial')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {assignment.deviceManufacturerNumber}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('assignments.installationDate')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(assignment.installedAt)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('assignments.removedAt')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(assignment.removedAt)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('assignments.status')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {assignment.removedAt === null ? t('common.status.active') : t('common.status.inactive')}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          {t('assignments.shifts')}
        </Typography>

        <ShiftsListWithOpenIndicator
          shifts={shifts}
          loading={loading}
          error={error}
        />
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'background.paper' }}>
        <Button onClick={onClose} color="primary">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentWithoutLicensePlateDetailDialog;

